//components/careers-list.js
import { memo } from "react";
import ClipLoader from "react-spinners/ClipLoader";
import PropTypes from "prop-types";
import { useTranslation } from "next-i18next";
import Link from "next/link";
import { useRouter } from "next/router";
import Pagination from "../utils/pagination";

const CareersList = memo(({ careers = [], loading, page, totalPages }) => {
  const { t } = useTranslation(["careers", "common"]);
  const router = useRouter();
  const lang = router.locale || "en";

  // Clean URLs only: no ?page=1 ever.
  const pageHref = (p) => {
    const query = {};
    if (p > 1) query.page = p;
    return { pathname: router.pathname, query };
  };

  return (
    <section className="w-full px-6 sm:px-6 lg:px-20 py-[40px] lg:py-[60px] box-border text-text-primary max-w-[1600px] mx-auto">
      <h2 className="w-full m-0 text-[28px] sm:text-[32px] lg:text-[40px] leading-[120%] font-medium mb-6 md:mb-10">
        {t("careers:careers.list.title")}
      </h2>

      {loading ? (
        <div className="flex items-center justify-center w-full py-20">
          <ClipLoader color="#EF4444" size={48} />
        </div>
      ) : careers.length > 0 ? (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 md:gap-10">
            {careers.map((career) => (
              <Link
                key={career?._id}
                href={`/careers/${career?.slug}`}
                prefetch={false}
                style={{ textDecoration: "none" }}
                className="no-underline hover:no-underline focus:no-underline"
                aria-label={career?.position || "Open career"}
              >
                <div className="flex items-center rounded-[16px] justify-center border border-solid border-gray-500 p-6">
                  <div className="flex flex-col items-start justify-start w-full h-full gap-6">
                    <h3 className="text-[24px] font-semibold leading-[140%] m-0">
                      {career?.position}
                    </h3>

                    <p className="text-[16px] leading-[150%] m-0">
                      <span className="block mb-2">
                        {career?.jobType} {career?.location}
                      </span>{" "}
                      {career?.workLocationType}
                    </p>

                    {/* Button stays as-is visually, but is also a Link */}
                    <span className="rounded-[50px] bg-black text-white text-[16px] font-medium py-3 px-5 cursor-pointer hover:bg-[#e72c46] transition duration-300 ease-in-out no-underline hover:no-underline focus:no-underline">
                      {t("common:btnTxt.learnMore")}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </>
      ) : (
        <div className="py-20 text-lg text-center text-gray-400 col-span-full">
          No careers found.
        </div>
      )}

      <Pagination
        meta={{ page, totalPages: totalPages }}
        locale={lang}
        hrefForPage={pageHref}
        ariaLabel="Careers pagination"
      />
    </section>
  );
});

CareersList.propTypes = {
  careers: PropTypes.array,
  loading: PropTypes.bool,
  page: PropTypes.number,
  totalPages: PropTypes.number,
};

export default CareersList;
